import React, {
  Children,
  PropsWithChildren,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Outlet, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Stack } from 'react-bootstrap';

import TitleSemiBold from 'components/Typography/TitleSemiBold';
import Button from 'components/DesignSystem/Button';
import shortenedTabPath from 'helper/shortenedTabPath';
import TabbedPageTab from './Tab';

import './index.scss';

export default function TabbedPage({
  children,
  setControlPanelVisibility,
}: PropsWithChildren<{
  setControlPanelVisibility?: (value: React.SetStateAction<boolean>) => void;
}>) {
  const navs = useRef<HTMLDivElement>(null);

  const [scroll, setScroll] = useState(0);
  const [scrollMax, setScrollMax] = useState(0);
  const location = useLocation();

  const tabs = Children.toArray(children) as React.ReactElement<any>[];

  const path = shortenedTabPath(location.pathname);
  const activeTab = tabs.find((tab) => path === tab.props.path);
  const activeTabPath = activeTab?.props?.path;
  const showControlPanel = activeTab?.props?.showControlPanel || false;

  useEffect(() => {
    if (setControlPanelVisibility) setControlPanelVisibility(showControlPanel);
  }, [showControlPanel, setControlPanelVisibility]);

  useEffect(() => {
    if (navs.current) navs.current.scrollLeft = scroll;
  }, [scroll]);

  useEffect(() => {
    if (scroll > scrollMax) setScroll(scrollMax);
  }, [scroll, scrollMax, setScroll]);

  useEffect(() => {
    const tabsDiv = navs.current;

    if (tabsDiv === null) return () => {};

    const resizeObserver = new ResizeObserver(() => {
      setScrollMax(tabsDiv.scrollWidth - tabsDiv.clientWidth);
    });
    resizeObserver.observe(tabsDiv);

    return () => { resizeObserver.disconnect(); };
  }, [scrollMax]);

  return (
    <div className="tabbed-page">
      <Stack ref={navs} direction="horizontal" id="scroll-navs" gap={1} className="nav nav-tabs">
        {scroll > 0 && (
          <Button
            type="secondary"
            icon="left_arrow"
            small
            className="scroll-button scroll-left"
            onClick={() => setScroll(scroll - 300)}
          />
        )}
        {tabs.map((tab) => (
          <Link
            key={tab.props.path}
            to={tab.props.path}
            className={`nav-link ${tab.props.path === activeTabPath ? 'active' : ''}`}
          >
            <TitleSemiBold>
              {tab.props.title}
            </TitleSemiBold>
          </Link>
        ))}
        {scroll < scrollMax && (
          <Button
            type="secondary"
            icon="right_arrow"
            small
            className="scroll-button scroll-right"
            onClick={() => setScroll(scroll + 300)}
          />
        )}
      </Stack>
      <div className="tabbed-page-content">
        <Outlet />
      </div>
    </div>
  );
}

TabbedPage.Tab = TabbedPageTab;
